/**
 * Cart Drawer Controller (OS 2.0 Native Ajax & Section Rendering)
 * - Server-renders cart-drawer via Section Rendering API
 * - Updates header badge directly from authoritative Shopify item_count
 * - Uses locale-aware window.Shopify.routes.root
 * - Uses delegated event listeners for robust re-render resilience
 */

class CartDrawerManager {
  constructor() {
    this.isOpen = false;
    this.previousFocus = null;
    this.init();
  }

  get routes() {
    const root = window.Shopify?.routes?.root || '/';
    const cleanRoot = root.endsWith('/') ? root : `${root}/`;
    return {
      cart_add_url: `${cleanRoot}cart/add.js`,
      cart_change_url: `${cleanRoot}cart/change.js`,
      cart_url: `${cleanRoot}cart.js`
    };
  }

  get drawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  get overlay() {
    return document.querySelector('[data-cart-overlay]');
  }

  init() {
    // Delegated click listener on document for maximum resilience across DOM replacements
    document.addEventListener('click', (e) => {
      // 1. Open trigger (header cart icon)
      const openBtn = e.target.closest('[data-cart-drawer-trigger]');
      if (openBtn) {
        e.preventDefault();
        this.open();
        return;
      }

      // 2. Close trigger
      const closeBtn = e.target.closest('[data-cart-close]');
      if (closeBtn) {
        e.preventDefault();
        this.close();
        return;
      }

      // 3. Overlay click closes drawer
      if (e.target.matches('[data-cart-overlay]')) {
        e.preventDefault();
        this.close();
        return;
      }

      // 4. Quantity modification (+ / -)
      const qtyBtn = e.target.closest('[data-cart-qty-btn]');
      if (qtyBtn) {
        e.preventDefault();
        const lineKey = qtyBtn.getAttribute('data-line-key');
        const change = parseInt(qtyBtn.getAttribute('data-qty-change'), 10);
        const currentQty = parseInt(qtyBtn.getAttribute('data-current-qty'), 10);
        const newQty = Math.max(0, currentQty + change);
        this.updateQuantity(lineKey, newQty);
        return;
      }

      // 5. Item removal
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        const lineKey = removeBtn.getAttribute('data-line-key');
        this.updateQuantity(lineKey, 0);
        return;
      }

      // 6. Upsell add-to-cart button
      const upsellBtn = e.target.closest('[data-cart-upsell-btn]');
      if (upsellBtn) {
        e.preventDefault();
        const variantId = upsellBtn.getAttribute('data-variant-id');
        if (variantId) {
          this.addVariantDirectly(variantId, 1);
        }
      }
    });

    // Keyboard ESC listener
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
      if (e.key === 'Tab' && this.isOpen) {
        this.keepFocusInside(e);
      }
    });

    // Delegated form submission listener for Ajax cart
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (form) {
        e.preventDefault();
        this.submitProductForm(form);
      }
    });
  }

  open() {
    const drawer = this.drawer;
    if (!drawer) return;
    if (!this.isOpen) this.previousFocus = document.activeElement;
    this.isOpen = true;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('is-locked');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));

    // Focus close button or first interactive element
    const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusable?.focus();
  }

  close() {
    const drawer = this.drawer;
    if (!drawer) return;
    this.isOpen = false;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.previousFocus?.focus();
  }

  keepFocusInside(event) {
    const drawer = this.drawer;
    if (!drawer) return;
    const focusable = [...drawer.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  showError(message) {
    const region = this.drawer?.querySelector('[data-cart-error]');
    if (!region) return;
    region.textContent = message;
    region.hidden = false;
    region.focus();
  }

  updateBadge(itemCount) {
    const count = typeof itemCount === 'number' ? itemCount : (parseInt(itemCount, 10) || 0);
    const badges = document.querySelectorAll('[data-cart-badge]');
    badges.forEach(badge => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  }

  renderCartDrawerSection(sectionHtml) {
    if (!sectionHtml) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const newContent = doc.querySelector('[data-cart-drawer]');

    const currentDrawer = this.drawer;
    if (newContent && currentDrawer) {
      currentDrawer.innerHTML = newContent.innerHTML;
    }
  }

  async fetchAndUpdateAuthoritativeCart() {
    try {
      const response = await fetch(this.routes.cart_url, {
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const cart = await response.json();
        this.updateBadge(cart.item_count);
        return cart;
      }
    } catch (err) {
      console.warn('Could not fetch authoritative cart:', err);
    }
  }

  async submitProductForm(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.innerHTML : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
    }

    try {
      const formData = new FormData(form);
      formData.append('sections', 'cart-drawer');

      const response = await fetch(this.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Errore durante l\'aggiunta al carrello.');
      }

      // 1. Re-render drawer from server response
      if (data.sections && data.sections['cart-drawer']) {
        this.renderCartDrawerSection(data.sections['cart-drawer']);
      }

      // 2. Obtain authoritative cart state to update badge
      await this.fetchAndUpdateAuthoritativeCart();

      // 3. Open drawer
      this.open();
    } catch (err) {
      this.open();
      this.showError(err.message || 'Si è verificato un errore.');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
        submitBtn.innerHTML = originalText;
      }
    }
  }

  async addVariantDirectly(variantId, quantity = 1) {
    try {
      const payload = {
        id: variantId,
        quantity: quantity,
        sections: 'cart-drawer'
      };

      const response = await fetch(this.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Errore aggiunta upsell.');
      }

      if (data.sections && data.sections['cart-drawer']) {
        this.renderCartDrawerSection(data.sections['cart-drawer']);
      }

      await this.fetchAndUpdateAuthoritativeCart();
      this.open();
    } catch (err) {
      this.open();
      this.showError(err.message || 'Impossibile aggiungere il prodotto al carrello.');
    }
  }

  async updateQuantity(lineKey, quantity) {
    if (!lineKey) return;
    this.setDrawerLoading(true);

    try {
      const payload = {
        id: lineKey,
        quantity: quantity,
        sections: 'cart-drawer'
      };

      const response = await fetch(this.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || 'Errore aggiornamento quantità.');
      }

      // 1. Re-render drawer from server response
      if (data.sections && data.sections['cart-drawer']) {
        this.renderCartDrawerSection(data.sections['cart-drawer']);
      }

      // 2. cart/change.js returns the full cart object with item_count directly
      if (typeof data.item_count === 'number') {
        this.updateBadge(data.item_count);
      } else {
        await this.fetchAndUpdateAuthoritativeCart();
      }
    } catch (err) {
      this.showError(err.message || 'Errore aggiornamento carrello.');
    } finally {
      this.setDrawerLoading(false);
    }
  }

  setDrawerLoading(isLoading) {
    const drawer = this.drawer;
    if (!drawer) return;
    if (isLoading) {
      drawer.classList.add('is-busy');
    } else {
      drawer.classList.remove('is-busy');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawerManager();
});
